import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Compass,
  Drill,
  Layers,
  Cog,
  TestTube2,
  Combine,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/dfuf65tx_exact%20sized%20transaprent%20file.png";

const RIG_ON_SITE =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/l7cghpsw_IMG-20250710-WA0009.jpg";
const RIG_TRANSPORT =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/4wl5phxn_IMG-20260122-WA0010.jpg";
const DRILL_BIT =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/wqqz3rkl_IMG-20251112-WA0002.jpg";
const SURVEY_MAP =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/rsoqphgi_IMG-20251029-WA0007.jpg";
const CRAWLER_RIG =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/ypcc02hu_Crawler%20Rigs-Drill-Rig-Borehole-300m-Deepwater-Well-Drilling-Machine.avif";
const SURVEY_KIT =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/ck9ai9i7_s-l1600%20%281%29.webp";
const SURVEY_DETECTOR =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/3wqjykys_s-l1600.webp";
const SURVEY_FIELD =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/md73uy4h_s-l1600%20%282%29.webp";

const WHATSAPP_NUMBER = "263774724222";
const PHONE_DISPLAY = "+263 774 724 222";
const PHONE_SECONDARY = "+263 71 945 6477";
const EMAIL = "info@mutumwa.co.zw";
const LOCATION = "1 Harvey Brown Ave, Milton Park, Harare, Zimbabwe";

// ─────────────────────────────────────────────────────────────
// Reveal hook
// ─────────────────────────────────────────────────────────────
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

// ─────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────
const SECTION_NAV = [
  { href: "#services", label: "Services" },
  { href: "#process", label: "Process" },
  { href: "#contracts", label: "Contracts" },
  { href: "#equipment", label: "Equipment" },
  { href: "#gallery", label: "Gallery" },
  { href: "#quote", label: "Quote" },
];

const Header = () => (
  <header
    data-testid="borehole-header"
    className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#F7F5F0]/80 border-b border-[rgba(26,47,76,0.12)]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
      <Link to="/" data-testid="borehole-nav-logo" className="flex items-center gap-3">
        <img src={LOGO} alt="Mutumwa" className="h-12 w-auto" />
      </Link>
      <nav className="hidden lg:flex items-center gap-8">
        <Link
          to="/"
          data-testid="borehole-nav-tanks"
          className="link-underline text-xs uppercase tracking-[0.18em] font-semibold text-[#1A2F4C]"
        >
          Tanks
        </Link>
        {SECTION_NAV.map((n) => (
          <a
            key={n.href}
            href={n.href}
            data-testid={`borehole-nav-${n.label.toLowerCase()}`}
            className="link-underline text-xs uppercase tracking-[0.18em] font-semibold text-[#1A2F4C]"
          >
            {n.label}
          </a>
        ))}
      </nav>
      <a href="#quote" data-testid="borehole-header-quote" className="btn-primary !py-3 !px-5 text-sm">
        Get a Quote <ArrowRight size={14} />
      </a>
    </div>
  </header>
);

// ─────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────
const Hero = () => {
  const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hello Mutumwa, I'd like a borehole drilling quote."
  )}`;
  return (
    <section data-testid="borehole-hero" className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center min-h-[80vh]">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Link
              to="/"
              data-testid="borehole-back-link"
              className="link-underline inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C] mb-8"
            >
              <ArrowLeft size={14} /> Mutumwa Tanks
            </Link>
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="h-[1px] w-10 bg-[#8B1E28]" />
              <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
                Mutumwa · Borehole Drilling
              </span>
            </div>
            <h1
              data-testid="borehole-heading"
              className="font-display font-black text-[#1A2F4C] tracking-tighter leading-[0.88] text-5xl md:text-6xl lg:text-[5.25rem] reveal"
            >
              Drill it. <em className="font-light italic">Pump it.</em>
              <br />
              Store it.
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-[#4A4E58] leading-relaxed reveal">
              From geophysical site survey to a fully fitted wellhead — Mutumwa drills, casts and
              pumps boreholes that pair seamlessly with our rotomoulded storage tanks.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 reveal">
              <a href="#quote" data-testid="borehole-hero-quote" className="btn-primary">
                Request a Site Survey <ArrowRight size={16} />
              </a>
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="borehole-hero-whatsapp"
                className="btn-outline"
              >
                <MessageCircle size={16} /> WhatsApp Sales
              </a>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg reveal">
              {[
                { k: "300m", v: "Max drill depth" },
                { k: "End-to-End", v: "Survey to pump" },
                { k: "Bundled", v: "With Mutumwa tanks" },
              ].map((s) => (
                <div key={s.v} className="border-t-2 border-[#1A2F4C] pt-3">
                  <div className="font-display font-black text-2xl md:text-3xl text-[#1A2F4C] leading-tight">
                    {s.k}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[#4A4E58] mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 reveal">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#EBE7DE] -z-10 translate-x-4 translate-y-4" />
              <img
                src={RIG_ON_SITE}
                alt="Mutumwa team inspecting the mud-drilling rig"
                className="w-full h-[420px] md:h-[560px] object-cover object-right"
                data-testid="borehole-hero-image"
              />
              <div className="absolute bottom-4 left-4 bg-[#F7F5F0] px-4 py-2 border border-[#1A2F4C]">
                <span className="font-display italic text-[#1A2F4C] text-sm">Mutumwa rig · Zimbabwe</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Services
// ─────────────────────────────────────────────────────────────
const SERVICES = [
  {
    id: "survey",
    icon: Compass,
    title: "Geophysical Site Survey",
    body: "Resistivity surveys to locate aquifers and recommend the optimal drill point — before you spend on drilling.",
  },
  {
    id: "drilling",
    icon: Drill,
    title: "Borehole Drilling",
    body: "Residential, commercial and agricultural drilling up to 300m. We specialise in mud drilling — engineered for soft rock formations where air-compressor rigs fail to complete the hole.",
  },
  {
    id: "casing",
    icon: Layers,
    title: "Casing & Screening",
    body: "Heavy-duty PVC and steel casings with screening to protect water quality and extend the life of the borehole.",
  },
  {
    id: "pump",
    icon: Cog,
    title: "Pump Installation",
    body: "Solar and electric submersible pumps sized to your yield and lift requirements — fitted, wired and tested on site.",
  },
  {
    id: "testing",
    icon: TestTube2,
    title: "Water Quality Testing",
    body: "Yield testing plus laboratory water analysis so you know exactly what you're drinking, irrigating or processing with.",
  },
  {
    id: "bundle",
    icon: Combine,
    title: "Borehole + Tank Bundle",
    body: "One project, one team: drilled borehole, pump and Mutumwa storage tank installed and commissioned together.",
    accent: true,
  },
];

const Services = () => (
  <section id="services" data-testid="borehole-services" className="py-24 md:py-32 bg-[#F7F5F0]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 reveal">
        <div>
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
            / 01 — Services
          </span>
          <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-none mt-4 max-w-2xl">
            From source to storage. <em className="italic font-light">One trusted partner.</em>
          </h2>
        </div>
        <p className="max-w-sm text-[#4A4E58]">
          Mutumwa handles every step of the borehole journey, then connects it directly to your
          rotomoulded tank. No middlemen. One accountable team.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const accent = s.accent;
          return (
            <article
              key={s.id}
              data-testid={`service-${s.id}`}
              className={`product-card reveal p-8 md:p-10 border min-h-[280px] flex flex-col justify-between ${
                accent
                  ? "bg-[#1A2F4C] text-[#F7F5F0] border-[#1A2F4C]"
                  : "bg-[#EBE7DE] text-[#1A2F4C] border-[rgba(26,47,76,0.15)]"
              }`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <Icon
                    size={26}
                    className={accent ? "text-[#E6B8BC]" : "text-[#8B1E28]"}
                  />
                  <span
                    className={`font-display italic text-sm ${
                      accent ? "text-[#F7F5F0]/70" : "text-[#1A2F4C]/70"
                    }`}
                  >
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">
                  {s.title}
                </h3>
                <p
                  className={`mt-4 leading-relaxed ${
                    accent ? "text-[#F7F5F0]/80" : "text-[#2C303A]"
                  }`}
                >
                  {s.body}
                </p>
              </div>
              {accent && (
                <a
                  href="#quote"
                  className="link-underline inline-flex items-center gap-1 mt-6 text-xs uppercase tracking-[0.2em] font-bold text-[#E6B8BC]"
                >
                  Request bundle quote <ArrowUpRight size={14} />
                </a>
              )}
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Process
// ─────────────────────────────────────────────────────────────
const STEPS = [
  {
    n: "01",
    title: "Site Survey",
    body: "We run a geophysical resistivity survey, map subsurface aquifers and pinpoint the highest-yield drill point.",
  },
  {
    n: "02",
    title: "Drilling",
    body: "Our crawler rig mobilises to site and drills through soil, weathered rock and bedrock to reach the aquifer.",
  },
  {
    n: "03",
    title: "Casing + Pump",
    body: "We line the hole with casing & screening, then install and wire a solar or electric submersible pump.",
  },
  {
    n: "04",
    title: "Storage + Test",
    body: "We connect to a Mutumwa tank, run yield + water-quality tests, and hand over a fully working system.",
  },
];

const Process = () => (
  <section
    id="process"
    data-testid="borehole-process"
    className="py-24 md:py-32 bg-[#1A2F4C] text-[#F7F5F0]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-3xl reveal">
        <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#E6B8BC]">
          / 02 — How it works
        </span>
        <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[0.95] mt-4">
          Four steps from <em className="italic font-light">underground to overhead.</em>
        </h2>
        <p className="mt-6 text-lg text-[#F7F5F0]/70 max-w-2xl">
          A clear sequence — no surprises, no hidden costs. We share findings at every stage and
          only drill when the survey says it&apos;s worth it.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6 mt-16">
        {STEPS.map((s, i) => (
          <div
            key={s.n}
            data-testid={`process-step-${s.n}`}
            className="reveal border-t-2 border-[#F7F5F0]/30 pt-6"
            style={{ transitionDelay: `${i * 100}ms` }}
          >
            <div className="font-display italic text-5xl text-[#F7F5F0]/80">{s.n}</div>
            <h3 className="font-display font-bold text-2xl mt-4">{s.title}</h3>
            <p className="mt-3 text-[#F7F5F0]/70 text-sm leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Equipment / Trust spread
// ─────────────────────────────────────────────────────────────
const Specialty = () => (
  <section data-testid="borehole-specialty" className="py-20 md:py-28 bg-[#8B1E28] text-[#F7F5F0] relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 reveal">
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#F7C9CC]">
            / Our specialty
          </span>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[0.95] mt-4">
            Where air-compressor rigs <em className="italic font-light">stop, we keep going.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-[#F7F5F0]/85 max-w-3xl leading-relaxed">
            Mutumwa specialises in <strong className="font-bold text-[#F7F5F0]">mud drilling</strong> —
            the right method for soft rock formations and unstable ground where conventional
            air-compressor rigs collapse, lose circulation, or simply fail to reach yield depth.
            If another driller has walked away from your site, we can usually finish the hole.
          </p>
        </div>
        <div className="lg:col-span-4 reveal">
          <div className="border-l-2 border-[#F7C9CC] pl-6 space-y-5">
            {[
              "Soft / collapsing formations",
              "Where air rigs lose circulation",
              "Stalled or abandoned holes",
              "High-yield aquifers in fine sand",
            ].map((b) => (
              <div key={b} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[#F7C9CC] mt-0.5 shrink-0" />
                <span className="text-[#F7F5F0]/95">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Contracts = () => (
  <section
    id="contracts"
    data-testid="borehole-contracts"
    className="py-24 md:py-32 bg-[#F7F5F0]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 reveal">
        <div>
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
            / How we contract
          </span>
          <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-none mt-4 max-w-2xl">
            Two ways to <em className="italic font-light">share the risk.</em>
          </h2>
        </div>
        <p className="max-w-sm text-[#4A4E58]">
          Choose the contract structure that suits your appetite for risk and your budget.
          Both are honoured by the same team, the same rig, and the same workmanship.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Dry contract */}
        <article
          data-testid="contract-dry"
          className="product-card bg-[#EBE7DE] border border-[rgba(26,47,76,0.15)] p-8 md:p-12 flex flex-col"
        >
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              Standard
            </span>
            <span className="font-display italic text-[#1A2F4C]/70 text-sm">01</span>
          </div>
          <h3 className="font-display font-black text-3xl md:text-5xl text-[#1A2F4C] tracking-tight leading-none">
            Dry Contract
          </h3>
          <p className="mt-5 text-[#2C303A] leading-relaxed">
            You pay for the drilling work itself — regardless of whether we strike water. The
            survey-led approach minimises this risk, but the financial exposure sits with you.
          </p>
          <ul className="mt-7 space-y-3 text-sm">
            {[
              "Lower upfront price",
              "Survey-led to maximise hit rate",
              "Payment on drilling completion",
              "Best when survey results are strong",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#1A2F4C]">
                <CheckCircle2 size={16} className="text-[#8B1E28] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <a
            href="#quote"
            data-testid="contract-dry-cta"
            className="link-underline inline-flex items-center gap-1 mt-8 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]"
          >
            Quote a Dry Contract <ArrowUpRight size={14} />
          </a>
        </article>

        {/* Wet contract */}
        <article
          data-testid="contract-wet"
          className="product-card bg-[#1A2F4C] text-[#F7F5F0] border border-[#1A2F4C] p-8 md:p-12 flex flex-col relative"
        >
          <span className="absolute top-6 right-6 bg-[#8B1E28] text-[#F7F5F0] text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
            On request
          </span>
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#E6B8BC]">
              Risk-shared
            </span>
            <span className="font-display italic text-[#F7F5F0]/70 text-sm">02</span>
          </div>
          <h3 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-none">
            Wet Contract
          </h3>
          <p className="mt-5 text-[#F7F5F0]/85 leading-relaxed">
            You pay <em className="italic">only if we strike water.</em> Mutumwa carries the
            drilling risk, which is why the rate is higher — but if the hole comes up dry, you
            owe nothing for the drilling itself.
          </p>
          <ul className="mt-7 space-y-3 text-sm">
            {[
              "No water, no charge for drilling",
              "Premium rate vs. dry contract",
              "Subject to site survey approval",
              "Best for high-uncertainty sites",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#F7F5F0]/90">
                <CheckCircle2 size={16} className="text-[#E6B8BC] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <a
            href="#quote"
            data-testid="contract-wet-cta"
            className="link-underline inline-flex items-center gap-1 mt-8 text-xs uppercase tracking-[0.2em] font-bold text-[#E6B8BC]"
          >
            Request a Wet Contract <ArrowUpRight size={14} />
          </a>
        </article>
      </div>

      <p className="mt-8 text-sm text-[#4A4E58] max-w-3xl">
        <strong className="text-[#1A2F4C]">Note:</strong> Wet contracts are issued at our
        discretion after reviewing the site survey. Sites with marginal survey results may be
        offered on dry-contract terms only.
      </p>
    </div>
  </section>
);



const Equipment = () => (
  <section id="equipment" data-testid="borehole-equipment" className="py-24 md:py-32 bg-[#EBE7DE]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 reveal">
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
            / 03 — Equipment
          </span>
          <h2 className="font-display font-black text-4xl md:text-5xl text-[#1A2F4C] tracking-tight leading-[0.95] mt-4">
            The right rig. <em className="italic font-light">The right bit.</em>
          </h2>
          <p className="mt-6 text-[#2C303A] leading-relaxed">
            We run a 300m-rated crawler drill rig paired with PDC and DTH bits — built to handle
            Zimbabwe&apos;s mixed geology, from sandy overburden to granite bedrock.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Mud drilling specialists — for soft formations where air-compressor rigs fail",
              "300m deepwater drilling capability",
              "PDC and DTH bit selection per geology",
              "On-site mobilisation across Zimbabwe",
              "Survey-led — we don't drill blind",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#1A2F4C]">
                <CheckCircle2 size={18} className="text-[#8B1E28] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7 reveal">
          <div className="grid grid-cols-2 gap-4">
            <div className="relative col-span-2">
              <img
                src={CRAWLER_RIG}
                alt="300m crawler drill rig"
                className="w-full h-[300px] md:h-[360px] object-cover"
              />
              <span className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                Crawler rig
              </span>
            </div>
            <div className="relative">
              <img
                src={DRILL_BIT}
                alt="PDC drill bit"
                className="w-full h-[220px] object-cover"
              />
              <span className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                PDC bit
              </span>
            </div>
            <div className="relative">
              <img
                src={SURVEY_MAP}
                alt="Geophysical resistivity survey result"
                className="w-full h-[220px] object-cover"
              />
              <span className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                Survey map
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Survey equipment row */}
      <div className="mt-16 md:mt-20 reveal">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              / Survey suite
            </span>
            <h3 className="font-display font-bold text-2xl md:text-4xl text-[#1A2F4C] tracking-tight leading-tight mt-2">
              PQWT GT150A · <em className="italic font-light">water detector</em>
            </h3>
          </div>
          <p className="hidden md:block max-w-sm text-sm text-[#4A4E58]">
            Natural-electric-field geophysical detector that maps water-bearing zones up to
            300m before we mobilise the rig.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5 relative">
            <img
              src={SURVEY_KIT}
              alt="PQWT survey equipment kit"
              className="w-full h-[260px] md:h-[340px] object-cover bg-white"
            />
            <span className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
              Field kit
            </span>
          </div>
          <div className="md:col-span-4 relative">
            <img
              src={SURVEY_DETECTOR}
              alt="PQWT detector showing live scan"
              className="w-full h-[260px] md:h-[340px] object-cover bg-white"
            />
            <span className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
              Live scan
            </span>
          </div>
          <div className="md:col-span-3 relative">
            <img
              src={SURVEY_FIELD}
              alt="Survey equipment on site in field"
              className="w-full h-[260px] md:h-[340px] object-cover"
            />
            <span className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
              On site
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const DRILLING_VIDEO =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/omgqoo9m_Rig%20drilling%20video.mp4";
const DRILLING_ACTION =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/zfvhtlup_IMG-20250821-WA0013.jpg";
const BOREHOLE_CASING =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/f6attph5_Borehole%20Casing.jpg";
const PUMP_TANK_INSTALL =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/qjldam8e_Tank%2C%20solar%20panels%20%26%20stand.png";

// ─────────────────────────────────────────────────────────────
// Gallery
// ─────────────────────────────────────────────────────────────
const Gallery = () => {
  const items = [
    { type: "video", src: DRILLING_VIDEO, span: "md:col-span-8", h: "h-[260px] md:h-[460px]", label: "Drilling · live footage" },
    { src: DRILLING_ACTION, span: "md:col-span-4", h: "h-[260px] md:h-[460px]", label: "Striking water" },
    { src: PUMP_TANK_INSTALL, span: "md:col-span-7", h: "h-[280px] md:h-[380px]", label: "Solar pump + Mutumwa tank" },
    { src: RIG_ON_SITE, span: "md:col-span-5", h: "h-[280px] md:h-[380px]", label: "Mutumwa rig" },
    { src: BOREHOLE_CASING, span: "md:col-span-4", h: "h-[260px] md:h-[300px]", label: "uPVC casing" },
    { src: RIG_TRANSPORT, span: "md:col-span-4", h: "h-[260px] md:h-[300px]", label: "Mobilisation" },
    { src: SURVEY_MAP, span: "md:col-span-4", h: "h-[260px] md:h-[300px]", label: "Resistivity survey" },
    { src: DRILL_BIT, span: "md:col-span-6", h: "h-[260px] md:h-[300px]", label: "PDC bit" },
    { src: CRAWLER_RIG, span: "md:col-span-6", h: "h-[260px] md:h-[300px]", label: "Crawler rig" },
  ];
  return (
    <section id="gallery" data-testid="borehole-gallery" className="py-24 md:py-32 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14 reveal">
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
            / 04 — Gallery
          </span>
          <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-none mt-4">
            On-site. <em className="italic font-light">In the field.</em>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {items.map((it, i) => (
            <figure
              key={i}
              data-testid={`borehole-gallery-${i}`}
              className={`reveal relative overflow-hidden ${it.span}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              {it.type === "video" ? (
                <video
                  src={it.src}
                  poster={DRILLING_ACTION}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className={`w-full ${it.h} object-cover bg-[#1A2F4C]`}
                />
              ) : (
                <img
                  src={it.src}
                  alt={it.label}
                  className={`w-full ${it.h} object-cover transition-transform duration-700 hover:scale-105`}
                />
              )}
              <figcaption className="absolute bottom-3 left-3 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                {it.label}
                {it.type === "video" && (
                  <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#8B1E28] align-middle animate-pulse" />
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Quote form (pre-tagged Borehole Drilling)
// ─────────────────────────────────────────────────────────────
const Quote = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hello Mutumwa, I'd like a borehole drilling quote."
  )}`;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form, product_interest: "Borehole Drilling" };
      if (payload.quantity === "" || payload.quantity == null) {
        delete payload.quantity;
      } else {
        payload.quantity = parseInt(payload.quantity, 10);
      }
      await axios.post(`${API}/contact`, payload);
      toast.success("Enquiry received. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", quantity: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="borehole-quote" className="py-24 md:py-32 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              / 05 — Get a Quote
            </span>
            <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-[0.95] mt-4">
              Tell us about <em className="italic font-light">your site.</em>
            </h2>
            <p className="mt-6 text-[#4A4E58] text-lg leading-relaxed max-w-md">
              Share your location, intended use and any existing infrastructure. We&apos;ll come back
              within one working day with next steps and a survey timeline.
            </p>
            <div className="mt-10 space-y-4">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="borehole-quote-whatsapp"
                className="flex items-center gap-4 p-5 bg-[#8B1E28] text-[#F7F5F0] hover:bg-[#1A2F4C] transition-colors"
              >
                <MessageCircle size={22} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">
                    WhatsApp
                  </div>
                  <div className="font-display text-lg">Chat with sales</div>
                </div>
                <ArrowUpRight className="ml-auto" />
              </a>
              <a
                href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
                data-testid="borehole-quote-phone"
                className="flex items-center gap-4 p-5 border-2 border-[#1A2F4C] text-[#1A2F4C] hover:bg-[#1A2F4C] hover:text-[#F7F5F0] transition-colors"
              >
                <Phone size={22} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-70">
                    Call us
                  </div>
                  <div className="font-display text-lg leading-tight">{PHONE_DISPLAY}</div>
                  <div className="font-display text-sm opacity-80 leading-tight">{PHONE_SECONDARY}</div>
                </div>
                <ArrowUpRight className="ml-auto" />
              </a>
              <div className="flex items-start gap-4 pt-4 text-[#2C303A]">
                <Mail size={18} className="mt-1 text-[#8B1E28]" />
                <span>{EMAIL}</span>
              </div>
              <div className="flex items-start gap-4 text-[#2C303A]">
                <MapPin size={18} className="mt-1 text-[#8B1E28]" />
                <span>{LOCATION}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="borehole-form"
              className="bg-[#EBE7DE] p-8 md:p-12 border border-[rgba(26,47,76,0.15)]"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Name</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    data-testid="borehole-input-name"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Email</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    data-testid="borehole-input-email"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Phone</label>
                  <input
                    required
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+263 ..."
                    data-testid="borehole-input-phone"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                    Boreholes / sites
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    name="quantity"
                    value={form.quantity}
                    onChange={onChange}
                    placeholder="How many? (optional)"
                    data-testid="borehole-input-quantity"
                    className="input-editorial"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                    Site details
                  </label>
                  <textarea
                    required
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder="Location, intended use (domestic / agri / industrial), existing infrastructure..."
                    rows={4}
                    data-testid="borehole-input-message"
                    className="input-editorial resize-none"
                  />
                </div>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="borehole-submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Enquiry"} <ArrowRight size={16} />
                </button>
                <span className="text-xs text-[#4A4E58]">
                  Your enquiry is tagged &quot;Borehole Drilling&quot; automatically.
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Footer (compact, mirrors landing footer)
// ─────────────────────────────────────────────────────────────
const Footer = () => (
  <footer data-testid="borehole-footer" className="bg-[#1A2F4C] text-[#F7F5F0] pt-20 pb-6">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-[#F7F5F0]/15">
        <div className="md:col-span-5">
          <img src={LOGO} alt="Mutumwa" className="h-20 w-auto" />
          <p className="mt-6 text-[#F7F5F0]/70 max-w-sm leading-relaxed">
            Mutumwa drills boreholes and manufactures rotomoulded storage tanks — one trusted
            partner from source to storage.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#E6B8BC] mb-5">
            Explore
          </div>
          <ul className="space-y-3">
            <li>
              <Link to="/" className="link-underline text-[#F7F5F0]/90 text-sm">
                Tanks
              </Link>
            </li>
            {SECTION_NAV.map((n) => (
              <li key={n.href}>
                <a href={n.href} className="link-underline text-[#F7F5F0]/90 text-sm">
                  {n.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#E6B8BC] mb-5">
            Get in touch
          </div>
          <ul className="space-y-3 text-sm text-[#F7F5F0]/90">
            <li className="flex items-center gap-3"><Phone size={14} /> {PHONE_DISPLAY}</li>
            <li className="flex items-center gap-3"><Phone size={14} /> {PHONE_SECONDARY}</li>
            <li className="flex items-center gap-3"><Mail size={14} /> {EMAIL}</li>
            <li className="flex items-start gap-3"><MapPin size={14} className="mt-1 shrink-0" /> {LOCATION}</li>
          </ul>
        </div>
      </div>
      <div className="pt-16 pb-2">
        <h3 className="font-display italic font-black leading-none text-[22vw] md:text-[18vw] text-[#F7F5F0]/10 select-none tracking-tighter">
          MUTUMWA
        </h3>
      </div>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mt-6 text-xs text-[#F7F5F0]/50 uppercase tracking-[0.18em]">
        <span>© {new Date().getFullYear()} Mutumwa. All rights reserved.</span>
        <span>Made in Zimbabwe · Built to last</span>
      </div>
    </div>
  </footer>
);

// ─────────────────────────────────────────────────────────────
// Page root
// ─────────────────────────────────────────────────────────────
export default function BoreholeDrilling() {
  useReveal();
  return (
    <div className="App grain-overlay relative">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <Hero />
        <Services />
        <Process />
        <Specialty />
        <Equipment />
        <Gallery />
        <Quote />
      </main>
      <Footer />
    </div>
  );
}
